/**
 * Predefined medical/health source domains
 * These are known trusted sources that users can quickly select
 */
export const PREDEFINED_SOURCES = [
  // Major medical databases
  { domain: 'pubmed.ncbi.nlm.nih.gov', label: 'PubMed', category: 'Medical Databases' },
  {
    domain: 'cochranelibrary.com',
    label: 'Cochrane Library',
    category: 'Medical Databases',
  },
  { domain: 'uptodate.com', label: 'UpToDate', category: 'Medical Databases' },

  // Government health agencies
  { domain: 'nih.gov', label: 'NIH', category: 'Government' },
  { domain: 'cdc.gov', label: 'CDC', category: 'Government' },
  { domain: 'fda.gov', label: 'FDA', category: 'Government' },
  { domain: 'who.int', label: 'WHO', category: 'Government' },
  { domain: 'medlineplus.gov', label: 'MedlinePlus', category: 'Government' },

  // Medical institutions
  { domain: 'mayoclinic.org', label: 'Mayo Clinic', category: 'Medical Institutions' },
  {
    domain: 'clevelandclinic.org',
    label: 'Cleveland Clinic',
    category: 'Medical Institutions',
  },

  // Medical journals
  { domain: 'nejm.org', label: 'NEJM', category: 'Journals' },
  { domain: 'jamanetwork.com', label: 'JAMA Network', category: 'Journals' },
  { domain: 'thelancet.com', label: 'The Lancet', category: 'Journals' },
  { domain: 'bmj.com', label: 'BMJ', category: 'Journals' },
  { domain: 'nature.com', label: 'Nature', category: 'Journals' },

  // Publishers
  { domain: 'sciencedirect.com', label: 'ScienceDirect', category: 'Publishers' },
  { domain: 'springer.com', label: 'Springer', category: 'Publishers' },
  { domain: 'wiley.com', label: 'Wiley', category: 'Publishers' },

  // Professional organizations
  { domain: 'aafp.org', label: 'AAFP', category: 'Organizations' },
  { domain: 'ama-assn.org', label: 'AMA', category: 'Organizations' },

  // Health information
  { domain: 'webmd.com', label: 'WebMD', category: 'Health Info' },
  { domain: 'healthline.com', label: 'Healthline', category: 'Health Info' },
] as const;

export type PredefinedSource = (typeof PREDEFINED_SOURCES)[number];

/**
 * Get all unique categories from predefined sources
 */
export const SOURCE_CATEGORIES = [
  ...new Set(PREDEFINED_SOURCES.map((s) => s.category)),
] as const;

/**
 * Get predefined sources grouped by category
 */
export function getSourcesByCategory(): Record<string, PredefinedSource[]> {
  const result: Record<string, PredefinedSource[]> = {};
  for (const source of PREDEFINED_SOURCES) {
    const category = source.category;
    if (!result[category]) {
      result[category] = [];
    }
    result[category].push(source);
  }
  return result;
}

/**
 * Find a predefined source by domain
 */
export function findPredefinedSource(
  domain: string | undefined
): PredefinedSource | undefined {
  if (!domain) return undefined;
  const normalizedDomain = domain.toLowerCase().replace(/^www\./, '');
  return PREDEFINED_SOURCES.find((s) => s.domain === normalizedDomain);
}
